import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { TrendingUp, TrendingDown, Target, BarChart3, Activity } from "lucide-react";
import api from "@/lib/api";

const fmtPnl = (v) => {
  if (v == null) return '$0.00';
  const sign = v > 0 ? '+' : v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString(undefined, {minimumFractionDigits:2,maximumFractionDigits:2})}`;
};
const pnlCls = (v) => v > 0 ? 'ops-long' : v < 0 ? 'ops-short' : 'text-[hsl(var(--ops-text))]';

function MetricBox({ label, value, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-0.5">{label}</div>
      <div className={`text-base font-mono-num font-semibold ${color || 'text-[hsl(var(--ops-text))]'}`}>{value}</div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-[hsl(var(--ops-surface-2))] border border-[hsl(var(--ops-border))] rounded-lg px-3 py-2 text-xs">
        <div className="text-[hsl(var(--ops-text-muted))] mb-1">{label}</div>
        {payload.map((p, i) => (
          <div key={i} className={`font-mono-num ${p.value >= 0 ? 'ops-long' : 'ops-short'}`}>{fmtPnl(p.value)}</div>
        ))}
      </div>
    );
  }
  return null;
};

export default function Performance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: d } = await api.get("/ops/performance");
        setData(d);
      } catch (e) { /* silent */ }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return (
    <div className="space-y-4"><Skeleton className="h-8 w-48 bg-[hsl(var(--ops-surface))]" /><Skeleton className="h-64 bg-[hsl(var(--ops-surface))]" /><Skeleton className="h-48 bg-[hsl(var(--ops-surface))]" /></div>
  );

  if (!data || data.total_trades === 0) return (
    <div className="flex items-center justify-center h-64 text-[hsl(var(--ops-text-muted))] text-sm">No performance data available</div>
  );

  return (
    <div data-testid="ops-performance-page" className="space-y-5">
      <div>
        <h1 className="text-lg font-display font-semibold text-[hsl(var(--ops-text))]">Performance</h1>
        <p className="text-xs text-[hsl(var(--ops-text-muted))]">Trading performance metrics and analytics</p>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
          <MetricBox label="Total P&L" value={fmtPnl(data.total_pnl)} color={pnlCls(data.total_pnl)} />
        </Card>
        <Card className="p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
          <MetricBox label="Win Rate" value={`${data.win_rate}%`} color={data.win_rate >= 50 ? 'ops-long' : 'ops-short'} />
        </Card>
        <Card className="p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
          <MetricBox label="Profit Factor" value={data.profit_factor} color={data.profit_factor >= 1 ? 'ops-long' : 'ops-short'} />
        </Card>
        <Card className="p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
          <MetricBox label="Total Trades" value={data.total_trades} />
        </Card>
        <Card className="p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
          <MetricBox label="Max Drawdown" value={`${data.max_drawdown_pct}%`} color="ops-short" />
        </Card>
        <Card className="p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
          <MetricBox label="YTD P&L" value={fmtPnl(data.ytd_pnl)} color={pnlCls(data.ytd_pnl)} />
        </Card>
      </div>

      {/* Equity Curve */}
      <Card className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] p-4">
        <div className="text-sm font-semibold text-[hsl(var(--ops-text))] mb-3 flex items-center gap-2">
          <TrendingUp className="size-4 text-[hsl(var(--ops-brand))]" /> Account Growth
        </div>
        <div className="h-[250px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.equity_curve}>
              <defs>
                <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--ops-brand))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--ops-brand))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ops-gridline))" />
              <XAxis dataKey="date" tick={{ fill: 'hsl(var(--ops-text-muted))', fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: 'hsl(var(--ops-text-muted))', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="equity" stroke="hsl(var(--ops-brand))" fill="url(#eqGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* P&L Breakdown */}
      <Tabs defaultValue="daily">
        <div className="flex items-center justify-between mb-3 max-sm:flex-wrap max-sm:gap-2">
          <span className="text-sm font-semibold text-[hsl(var(--ops-text))] flex items-center gap-2">
            <BarChart3 className="size-4 text-[hsl(var(--ops-brand))]" /> P&L Breakdown
          </span>
          <TabsList className="bg-[hsl(var(--ops-surface))] border border-[hsl(var(--ops-border))]">
            <TabsTrigger value="daily" className="text-xs data-[state=active]:bg-[hsl(var(--ops-surface-2))] data-[state=active]:text-[hsl(var(--ops-text))]">Daily</TabsTrigger>
            <TabsTrigger value="weekly" className="text-xs data-[state=active]:bg-[hsl(var(--ops-surface-2))] data-[state=active]:text-[hsl(var(--ops-text))]">Weekly</TabsTrigger>
            <TabsTrigger value="monthly" className="text-xs data-[state=active]:bg-[hsl(var(--ops-surface-2))] data-[state=active]:text-[hsl(var(--ops-text))]">Monthly</TabsTrigger>
          </TabsList>
        </div>
        {['daily', 'weekly', 'monthly'].map(period => (
          <TabsContent key={period} value={period}>
            <Card className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] p-4">
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data[`${period}_pnl`]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ops-gridline))" />
                    <XAxis dataKey={period === 'daily' ? 'date' : period === 'weekly' ? 'week' : 'month'} tick={{ fill: 'hsl(var(--ops-text-muted))', fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fill: 'hsl(var(--ops-text-muted))', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v.toFixed(0)}`} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={0} stroke="hsl(var(--ops-text-muted))" strokeDasharray="2 2" />
                    <Bar dataKey="pnl" radius={[2, 2, 0, 0]} fill="hsl(var(--ops-brand))" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Win/Loss Stats */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
          <div className="text-sm font-semibold text-[hsl(var(--ops-text))] mb-3">Win / Loss Distribution</div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-[hsl(var(--ops-long))]/10">
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-long))] mb-1">Wins</div>
              <div className="text-xl font-mono-num font-bold text-[hsl(var(--ops-long))]">{data.wins}</div>
              <div className="text-xs text-[hsl(var(--ops-text-muted))] mt-1">Avg: {fmtPnl(data.avg_win)}</div>
            </div>
            <div className="p-3 rounded-lg bg-[hsl(var(--ops-short))]/10">
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-short))] mb-1">Losses</div>
              <div className="text-xl font-mono-num font-bold text-[hsl(var(--ops-short))]">{data.losses}</div>
              <div className="text-xs text-[hsl(var(--ops-text-muted))] mt-1">Avg: {fmtPnl(data.avg_loss)}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
          <div className="text-sm font-semibold text-[hsl(var(--ops-text))] mb-3">Profit Metrics</div>
          <div className="space-y-3">
            <div className="flex justify-between text-xs">
              <span className="text-[hsl(var(--ops-text-muted))]">Gross Profit</span>
              <span className="font-mono-num ops-long">{fmtPnl(data.gross_profit)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-[hsl(var(--ops-text-muted))]">Gross Loss</span>
              <span className="font-mono-num ops-short">{fmtPnl(-data.gross_loss)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-[hsl(var(--ops-text-muted))]">Profit Factor</span>
              <span className={`font-mono-num font-semibold ${data.profit_factor >= 1 ? 'ops-long' : 'ops-short'}`}>{data.profit_factor}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-[hsl(var(--ops-text-muted))]">Max Drawdown</span>
              <span className="font-mono-num ops-short">{data.max_drawdown_pct}%</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-[hsl(var(--ops-text-muted))]">Today P&L</span>
              <span className={`font-mono-num font-semibold ${pnlCls(data.today_pnl)}`}>{fmtPnl(data.today_pnl)}</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
